import React from 'react';
import { Pressable, StyleSheet, StyleProp, ViewStyle } from 'react-native';

import Text from '../Text';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import { FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    // Translated via the core Text component (i18n key or plain text).
    label: string;
    selected?: boolean;
    onPress?: () => void;
    disabled?: boolean;
    size?: 'sm' | 'md';
    // Overrides the pill color (e.g. a status color); defaults to the brand accent when selected.
    color?: string;
    containerStyle?: StyleProp<ViewStyle>;
};

// A pill: selectable filter chip (onPress + selected) or a static badge (color, no onPress).
export default function Chip({ label, selected, onPress, disabled, size = 'md', color, containerStyle }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    const activeColor = color ?? theme.button;
    const isBadge = !onPress;
    const filled = isBadge || selected;

    return (
        <Pressable
            onPress={onPress}
            disabled={disabled || isBadge}
            style={({ pressed }) => [
                styles.base,
                size === 'sm' ? styles.baseSm : null,
                filled ? { backgroundColor: activeColor, borderColor: activeColor } : styles.outline,
                pressed && !isBadge ? styles.pressed : null,
                disabled ? styles.disabled : null,
                containerStyle,
            ]}
        >
            <Text
                fontType={filled ? 'LATO_BOLD' : 'LATO_REGULAR'}
                style={StyleSheet.flatten([
                    styles.label,
                    size === 'sm' ? styles.labelSm : null,
                    filled ? styles.labelFilled : null,
                ])}
            >
                {label}
            </Text>
        </Pressable>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        base: {
            alignSelf: 'flex-start',
            borderRadius: scaleSize(14),
            borderWidth: 1,
            paddingHorizontal: scaleSize(12),
            paddingVertical: scaleSize(6),
        },
        baseSm: {
            borderRadius: scaleSize(10),
            paddingHorizontal: scaleSize(8),
            paddingVertical: scaleSize(3),
        },
        outline: {
            borderColor: theme.tabIconDefault,
            backgroundColor: 'transparent',
        },
        pressed: {
            opacity: 0.6,
        },
        disabled: {
            opacity: 0.4,
        },
        label: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
        },
        labelSm: {
            fontSize: FONT_SIZE_XS,
        },
        labelFilled: {
            color: theme.white,
        },
    });
